import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {MatRadioChange} from '@angular/material/radio';
import {forkJoin, merge, Observable, Observer, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, take, takeUntil} from 'rxjs/operators';
import {CaseType} from '../../../../types';
import {HelpInfo} from '../../../../types/help-info.enum';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {PolicyDTO} from '../../../../../ent/models/PolicyDTO';
import {ProductDTO} from '../../../../../ent/models/ProductDTO';
import {InsuredPersonDTO} from '../../../../../ent/models/InsuredPersonDTO';
import {SharedClmService} from '../../../../services/shared-clm.service';
import {InsuredPersonWithDatesDTO} from '../../../../../ent/models/InsuredPersonWithDatesDTO';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';
import {ClmRequestBuilder} from '../../../../services/clm-request-builder';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {Common, createNewPagedFilterDTO} from '../../../../../common/util/Util';
import {LookupValue} from '../../../../types/lookup-value.enum';
import {SharedService} from '../../../../../common/services/shared-service';
import {PolicyService} from '../../../../../ent/services/policy.service';
import {ProductService} from '../../../../../ent/services/product.service';
import {InsuredPersonService} from '../../../../../ent/services/insured-person.service';
import {HttpApiService} from '../../../../../common/services/http-api-service';
import {SharedFormService} from '../../../../../common/services/shared-form-service';
import {PagedFilterDTONew} from '../../../../../common/models/PagedFilterDTO';
import {EnvironmentService} from '../../../../../common/services/environment-service';

enum SearchType {
  Member = 'Member Search',
  Membership = 'Membership Search'
}

@Component({
  selector: 'app-member-policy-product-link',
  templateUrl: './member-policy-product-link.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_add-view-edit-styles.scss',
    './member-policy-product-link.component.scss'
  ]
})
export class MemberPolicyProductLinkComponent implements OnChanges, OnInit, OnDestroy {
  readonly caseType = CaseType;
  readonly type: CaseType = CaseType.Medical; // PP is for Medical Type
  readonly dateFormat = DateUtil.dateFormat;
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly searchType = SearchType;
  readonly insuredPersonNameTextChanged$ = new Subject<string>();
  readonly membershipNumberTextChanged$ = new Subject<string>();

  filteredInsuredPersons: InsuredPersonDTO[];
  filteredMemberships: PolicyDTO[];
  products: ProductDTO[];
  memberOrMembershipFormControl: FormControl;
  insuredPersonNameFormControl: FormControl;
  membershipFormControl: FormControl;
  productFormControl: FormControl;

  @Input() isEditMode: boolean;

  // Note: Used for Authorisation allow user to capture the auth without a case for primarySubClass="Pharmacy"
  @Input() isAuthorisation: boolean;

  @Input() isProviderPortal = false;
  @Input() selectedPolicy: PolicyDTO;
  @Input() selectedProduct: ProductDTO;
  @Input() selectedInsuredPerson: InsuredPersonDTO;
  @Input() selectedInsuredPersonWithDates: InsuredPersonWithDatesDTO;
  @Input() enableSearchInputs = true;
  @Output() isValid: EventEmitter<boolean> = new EventEmitter();

  // We need to update parent to do call to fetch authorization
  // numbers ddn values in case of "Submit Claim" as it is *required for claim.
  @Output() selectedInsuredPersonNotify: EventEmitter<boolean> = new EventEmitter();

  @ContentChild('memberNameTpl') memberNameTpl: TemplateRef<any> | undefined;
  @ContentChild('memberAssociationTpl') memberAssociationTpl: TemplateRef<any> | undefined;
  @ContentChild('dateOfBirthTpl') dateOfBirthTpl: TemplateRef<any> | undefined;
  @ContentChild('genderTpl') genderTpl: TemplateRef<any> | undefined;
  @ContentChild('nationalIDTpl') nationalIDTpl: TemplateRef<any> | undefined;

  constructor(
    public datePipe: DatePipe,
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private sharedClmService: SharedClmService,
    private sharedFormService: SharedFormService,
    private environmentService: EnvironmentService,
    private httpService: HttpApiService,
    private policyService: PolicyService,
    private productService: ProductService,
    private insuredPersonService: InsuredPersonService,
  ) {}

  get isPersonAndMembershipSelected() {
    return !!this.selectedInsuredPerson && !!this.selectedPolicy;
  }

  get hasProducts(): boolean {
    return !!this.products?.length;
  }

  getProductDisplayName(product: ProductDTO) {
    return `${product.productCode}: ${product.name} (${product.productStartDateForPerson} -
        ${product.productEndDateForPerson !== null ? product.productEndDateForPerson : ''})`;
  }

  createDateRangeString(startDate, endDate): string {
    return this.sharedClmService.createDateRangeString(startDate, endDate);
  }

  getAssociationDisplayString(selectedInsuredPerson: InsuredPersonDTO): string {
    return selectedInsuredPerson != null && selectedInsuredPerson.association != null ? selectedInsuredPerson.association.name : 'Principal';
  }

  getInsuredPersonNameDisplayString(insuredPerson: InsuredPersonDTO): string {
    const middleName = insuredPerson.middleName != null && insuredPerson.middleName.trim() != '' ? ` ${insuredPerson.middleName.trim()}` : '';
    return `${insuredPerson.firstName}${middleName} ${insuredPerson.lastName}`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedInsuredPerson && changes.selectedInsuredPerson.currentValue) {
      const displayLastName = this.getInsuredPersonDisplayValue(this.selectedInsuredPerson);
      this.insuredPersonNameFormControl.setValue(displayLastName);
    }

    if (changes.selectedPolicy && changes.selectedPolicy.currentValue) {
      this.membershipFormControl.setValue(this.getMembershipDisplayString(this.selectedPolicy));
    }

    if (changes.selectedProduct && changes.selectedProduct.currentValue) {
      this.productFormControl.setValue(this.selectedProduct?.productId);
    }

    const isFirstChange = Object.values(changes).some(change => change.firstChange);

    setTimeout(() => {
      // Note:
      // This is working but it can be further improve when proper handling done to get
      // all inputs data within a component instead of getting data from parent component as @Input
      // Set Value in this block to let `initFormControls` to run first for controls initialization
      this.updateData();
      if (isFirstChange) {
        this.memberOrMembershipFormControl.setValue(this.searchType.Membership);
      }
    }, 0);
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.initHelpInfo();
    this.initFormControls();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initHelpInfo() {

    // Since PP is for Medical
    this.getHelpInfo(HelpInfo.GeneralCase);
    this.getHelpInfo(HelpInfo.HospitalCase);
  }

  initSubscriptions() {


    // ----------------------------------------
    this.insuredPersonNameTextChanged$.pipe(
      takeUntil(this.destroyed$),
      debounceTime(this.debounceTime),
      distinctUntilChanged()
    )
      .subscribe((value: string) => {
          this.getListOfInsuredPersons(value);
        },
        error => console.log(error));

    this.membershipNumberTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime),
        distinctUntilChanged()
      )
      .subscribe((value: string) => {
          this.getListOfMemberships(value);
        },
        error => console.log(error));
  }

  initFormControls() {
    this.insuredPersonNameFormControl = new FormControl({value: null, disabled: true}, [Validators.required]);
    this.membershipFormControl = new FormControl(null, [Validators.required]);
    this.productFormControl = new FormControl({value: null, disabled: true}, [Validators.required]);
    this.memberOrMembershipFormControl = new FormControl(this.searchType.Membership);


    // Emit Updated FormGroup controls
    merge(
      this.insuredPersonNameFormControl.valueChanges,
      this.membershipFormControl.valueChanges,
      this.productFormControl.valueChanges,
      this.memberOrMembershipFormControl.valueChanges,
    )
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(_ => {
        const isFormValid = this.checkFormValidity();
        this.isValid.next(isFormValid);
      });
  }

  checkFormValidity(): boolean {
    return (
      this.insuredPersonNameFormControl.valid &&
      this.membershipFormControl.valid &&
      this.productFormControl.valid
    );
  }

  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onInsuredPersonSelected(name: string) {
    this.selectedInsuredPerson = this.filteredInsuredPersons?.find(
      (insuredPerson: InsuredPersonDTO) =>
        name === insuredPerson.displayValue
    );

    if (this.selectedInsuredPerson) {
      this.selectedInsuredPersonNotify.emit(true);
    }

    this.resetAndToggleFieldOnInsuredPersonSelected();
    this.updateData();
  }

  resetAndToggleFieldOnInsuredPersonSelected() {
    const memberOrMembershipRadioOption = this.memberOrMembershipFormControl.value;

    if (memberOrMembershipRadioOption === this.searchType.Membership) {
      this.toggleProductField();
    }
    else if (memberOrMembershipRadioOption === this.searchType.Member) {
      this.resetLinkedMembershipsAndProducts();
    }
  }

  onMembershipSelected(name: string) {
    this.selectedPolicy = this.filteredMemberships?.find(
      (item: PolicyDTO) => name === this.getMembershipDisplayString(item)
    );

    this.resetAndToggleFieldOnMembershipSelected();
    this.updateData();
  }

  resetAndToggleFieldOnMembershipSelected() {
    const memberOrMembershipRadioOption = this.memberOrMembershipFormControl.value;

    if (memberOrMembershipRadioOption === this.searchType.Membership) {
      this.resetInsuredPersonAndProduct();
    }
    else if (memberOrMembershipRadioOption === this.searchType.Member) {
      this.toggleProductField();
    }
  }

  onProductSelected(id: number) {
    if (this.products) {
      this.selectedProduct = this.products.find((item: ProductDTO) => id === item.productId);
    }
    this.updateData();
  }

  private autoSelectInsuredPerson(insuredPerson: InsuredPersonDTO): void {
    this.insuredPersonNameFormControl.setValue(insuredPerson.displayValue);
    this.onInsuredPersonSelected(insuredPerson.displayValue);
  }

  private autoSelectMembership(membership: PolicyDTO): void {
    const displayString = this.getMembershipDisplayString(membership);
    this.membershipFormControl.setValue(displayString);
    this.onMembershipSelected(displayString);
  }

  private autoSelectProduct(product: ProductDTO): void {
    this.productFormControl.setValue(product.productId);
    this.onProductSelected(product.productId);
  }

  onBlurInsuredPersonName() {
    const value = this.insuredPersonNameFormControl.value;
    const isMatched = this.filteredInsuredPersons?.find(
        (item: InsuredPersonDTO) =>
          value === item.displayValue
      ) !== undefined;

    if (!isMatched) {
      this.clearInsuredPersonName();
    }

    this.clearInsuredPersonWithDates();
  }

  onBlurMembershipNumber() {
    const value = this.membershipFormControl.value;
    const isMatched = this.filteredMemberships?.find(
        (item: PolicyDTO) => value === this.getMembershipDisplayString(item)
     ) !== undefined;

    if (!isMatched) {
      this.clearMembership();
    }
  }

  resetDependentLinkedInsuredPersonsMembershipsAndProducts() {
    this.filteredInsuredPersons = [];
    this.clearInsuredPersonName();
    this.clearInsuredPersonWithDates();
    this.resetLinkedMembershipsAndProducts();
  }

  resetInsuredPersonAndProduct() {
    this.clearInsuredPersonName();
    this.clearProduct();
  }

  resetLinkedMembershipsAndProducts() {
    this.clearMembership();
    this.clearProduct();
  }

  clearInsuredPersonName() {
    const hadSelectedInsuredPerson = this.selectedInsuredPerson != null;

    this.insuredPersonNameFormControl.reset();
    this.selectedInsuredPerson = null;

    if (hadSelectedInsuredPerson) {
      this.selectedInsuredPersonNotify.emit(false);
    }

    const memberOrMembershipValue = this.memberOrMembershipFormControl.value;
    if (memberOrMembershipValue === this.searchType.Membership) {
      this.toggleProductField();

      if (this.selectedPolicy == null) {
        this.insuredPersonNameFormControl.disable();
        this.filteredInsuredPersons = [];
      }

      this.reloadPersonsAndProductsInEditMode();
    }
  }

  clearMembership() {
    this.membershipFormControl.reset();
    this.selectedPolicy = null;

    const memberOrMembershipValue = this.memberOrMembershipFormControl.value;

    if (memberOrMembershipValue === this.searchType.Member) {

      // Note: The linked memberships are associated with the selected "Insured Person"
      if (this.selectedInsuredPerson == null) {
        this.membershipFormControl.disable();
        this.filteredMemberships = [];
      }

      this.toggleProductField();
      this.reloadMembershipsAndProductsInEditMode();
    }
  }

  clearProduct() {
    this.productFormControl.reset();
    this.selectedProduct = null;

    const memberOrMembershipValue = this.memberOrMembershipFormControl.value;

    if (memberOrMembershipValue === this.searchType.Membership) {
      this.toggleProductField();

      if (this.selectedPolicy == null) {
        this.productFormControl.disable();
        this.products = [];
      }
      this.reloadPersonsAndProductsInEditMode();
    }
    else if (memberOrMembershipValue === this.searchType.Member) {
      if (this.selectedInsuredPerson == null) {
        this.productFormControl.disable();
        this.products = [];
      }

      this.reloadMembershipsAndProductsInEditMode();
    }
  }

  clearInsuredPersonWithDates() {
    this.selectedInsuredPersonWithDates = null;
  }

  reloadMembershipsAndProductsInEditMode() {
    if (this.isEditMode) {
      this.loadMembershipsAndProducts$().subscribe();
    }
  }

  reloadPersonsAndProductsInEditMode() {
    if (this.isEditMode) {
      this.loadPersonsAndProducts$().subscribe();
    }
  }

  onMemberOrMembershipRadioOptionValueChanged($event: MatRadioChange) {
    const value = $event.source.value;

    if (value === this.searchType.Membership) {
      this.membershipFormControl.enable();
      this.insuredPersonNameFormControl.disable();
    }
    else if (value === this.searchType.Member) {
      this.insuredPersonNameFormControl.enable();
      this.membershipFormControl.disable();
    }
    this.resetDependentLinkedInsuredPersonsMembershipsAndProducts();
  }

  getMembershipDisplayString(membership: PolicyDTO): string {
    return `${membership.name} - ${membership.number}`;
  }

  getMembershipDisplayStringWithDates(membership: PolicyDTO): string {
    const startDate = this.datePipe.transform(membership.startDate, this.dateFormat) ?? '';
    const endDate = membership.endDate != null ? this.datePipe.transform(membership.endDate, this.dateFormat) : '';
    return `${membership.name} - ${membership.number} (${startDate} - ${endDate})`;
  }

  getPayload() {
    return {
      insuredPerson: this.selectedInsuredPerson,
      policy: this.selectedPolicy,
      product: this.selectedProduct
    };
  }

  getSelectedOrOnlyPolicyNumber(): string | null {
    if (this.selectedPolicy?.number) {
      return this.selectedPolicy.number;
    }

    if (this.filteredMemberships?.length === 1) {
      return this.filteredMemberships[0].number;
    }

    return null;
  }

  private getProcessingClass(type: string): number {
    if (type === CaseType.Medical) {
      return 1;
    }
    else if (type === CaseType.Funeral) {
      return 3;
    }
  }

  private toggleProductField() {
    if (this.isPersonAndMembershipSelected) {
      this.productFormControl.enable();
    }
    else {
      this.productFormControl.disable();
    }
  }

  private getInsuredPersonDisplayValue(insuredPerson: InsuredPersonDTO): string {
    return `${insuredPerson.firstName} ${insuredPerson.lastName} - ${insuredPerson.cardNumber} - ${insuredPerson.nationalIdNumber}`;
  }

  private createInsuredPersonSearchFieldMap(primaryFieldName: string, value: string): {} {
    const primaryFilterDTO: PagedFilterDTONew = new PagedFilterDTONew();
    primaryFilterDTO.name = primaryFieldName;
    primaryFilterDTO.value = value;
    primaryFilterDTO.bracket = '(';

    const cardNumberFilterDTO: PagedFilterDTONew = new PagedFilterDTONew();
    cardNumberFilterDTO.name = 'cardNumber';
    cardNumberFilterDTO.value = value;
    cardNumberFilterDTO.logicGate = 'OR';
    cardNumberFilterDTO.bracket = ')';

    // Note: Provider portal matches on the exact value, everywhere else keeps the default match.
    if (this.isProviderPortal) {
      primaryFilterDTO.operator = '==';
      cardNumberFilterDTO.operator = '==';
    }

    const fieldMap = {};
    fieldMap[primaryFieldName] = primaryFilterDTO;
    fieldMap['cardNumber'] = cardNumberFilterDTO;

    return fieldMap;
  }

  private getListOfInsuredPersons(value: string): void {
    let pagedRequestDTO: PagedRequestDTO;

    if (new RegExp('^[0-9]+$').test(value) || value.includes('_')) {
      pagedRequestDTO = ClmRequestBuilder.getFilteredListOfInsuredPersonsByIdNumberPagedRequestDTO(value);
      pagedRequestDTO.fieldMap = this.createInsuredPersonSearchFieldMap('nationalIdNumber', value);
    }
    else {
      pagedRequestDTO = ClmRequestBuilder.getFilteredListOfInsuredPersonsPagedRequestDTO(value);
      pagedRequestDTO.fieldMap = this.createInsuredPersonSearchFieldMap('lastName', value);
    }

    this.insuredPersonService.getListOfInsuredPersons(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<InsuredPersonDTO>) => {
          response.resultList = response.resultList.map((person: InsuredPersonDTO) => {
            person.displayValue = this.getInsuredPersonDisplayValue(person);
            return person;
          });
          this.filteredInsuredPersons = response.resultList;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getListOfMemberships(value: string): void {
    let pagedRequestDTO: PagedRequestDTO;

    pagedRequestDTO = ClmRequestBuilder.getFilteredListOfPoliciesPagedRequestDTO(value);

    if (this.isProviderPortal) {
      const membershipNumberFilterDTO: PagedFilterDTONew = createNewPagedFilterDTO('number', '==', value);
      pagedRequestDTO.fieldMap[`number`] = membershipNumberFilterDTO;
    }

    this.policyService.getListOfPolicies(pagedRequestDTO)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: PagedResponseDTO<PolicyDTO>) => {
          response.resultList = response.resultList.map((membership: PolicyDTO) => {
            return membership;
          });
          this.filteredMemberships = response.resultList;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private updateData() {
    this.updateDropdownList();
    this.updateInsuredPersonWithDates();
  }

  private updateDropdownList() {
    const memberOrMembershipRadioOption = this.memberOrMembershipFormControl.value;
    if (memberOrMembershipRadioOption === this.searchType.Membership) {
      this.getPersonsAndProducts();
    }
    else if (memberOrMembershipRadioOption === this.searchType.Member) {
      this.getMembershipsAndProducts();
    }
  }

  private getMembershipsAndProducts() {
    this.loadMembershipsAndProducts$()
      .subscribe((IsLoaded: boolean) => {
        if (IsLoaded) {
          this.membershipFormControl.enable();

          if (!this.selectedPolicy && this.filteredMemberships.length === 1) {
            this.autoSelectMembership(this.filteredMemberships[0]);
            return;
          }

          // Note: Products are associated with the selected membership, so if no membership is
          // selected reload the existing memberships and their associated products.
          const isMembershipMatched = this.filteredMemberships.find((item: PolicyDTO) => this.getMembershipDisplayString(item) === this.membershipFormControl.value);
          if (this.selectedPolicy && !isMembershipMatched) {
            this.resetLinkedMembershipsAndProducts();
            this.sharedService.openSnackBar('The membership\'s data has changed since the case was created - please update the membership and product fields accordingly', 'Dismiss');
          }
          else if (this.isEditMode) {
            this.productFormControl.enable();
          }

          if (this.isPersonAndMembershipSelected && !this.selectedProduct && this.products?.length === 1) {
            this.autoSelectProduct(this.products[0]);
            return;
          }

          this.showNotifications();
        }
      }, (errorResponse) => {
        const error = errorResponse.error;
        return error.businessMessage;
      });
  }

  private getPersonsAndProducts() {
    this.loadPersonsAndProducts$()
      .subscribe((IsLoaded: boolean) => {
        if (IsLoaded) {
          this.insuredPersonNameFormControl.enable();

          if (!this.selectedInsuredPerson && this.filteredInsuredPersons.length === 1) {
            this.autoSelectInsuredPerson(this.filteredInsuredPersons[0]);
            return;
          }

          const isPersonMatched = this.filteredInsuredPersons.find((item: InsuredPersonDTO) => item.displayValue === this.insuredPersonNameFormControl.value);
          if (!isPersonMatched) {
            this.resetInsuredPersonAndProduct();
          }
          else if (this.isEditMode) {
            this.productFormControl.enable();
          }

          if (this.isPersonAndMembershipSelected && !this.selectedProduct && this.products?.length === 1) {
            this.autoSelectProduct(this.products[0]);
            return;
          }

          this.showNotifications();
        }
      }, (errorResponse) => {
        const error = errorResponse.error;
        return error.businessMessage;
      });
  }

  private updateInsuredPersonWithDates() {
    if (this.selectedInsuredPerson && this.selectedPolicy && this.selectedProduct) {
      this.insuredPersonService.getInsuredPersonWithDates(this.selectedInsuredPerson.insuredPersonId, this.selectedPolicy.policyId, this.selectedProduct.productId)
        .pipe(
          take(1)
        )
        .subscribe(
          (response: InsuredPersonWithDatesDTO) => {
            this.selectedInsuredPersonWithDates = response;
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
    }
    else {
      this.clearInsuredPersonWithDates();
    }
  }

  private loadPersonsAndProducts$() {
    return new Observable((observer: Observer<any>) => {
      if (!this.selectedPolicy) {
        observer.next(false);
        return;
      }

      if (this.selectedInsuredPerson != null) {
        return forkJoin(
          this.insuredPersonService.getPolicyLinkedPersons(this.selectedPolicy.policyId, this.selectedProduct ? this.selectedProduct.productId : null, this.getProcessingClass(this.type)),
          this.productService.getInsuredPersonLinkedProducts(this.selectedInsuredPerson.insuredPersonId, this.selectedPolicy ? this.selectedPolicy.policyId : null, this.getProcessingClass(this.type))
        )
          .pipe(
            take(1),
          )
          .subscribe((responses: [InsuredPersonDTO[], ProductDTO[]]) => {
              if (responses[0] && responses[0].length > 0) {
                responses[0].map((person: InsuredPersonDTO) => {
                  person.displayValue = this.getInsuredPersonDisplayValue(person);
                  return person;
                });
                this.filteredInsuredPersons = [...responses[0]];
              }
              else {
                this.filteredInsuredPersons = [];
              }

              if (responses[1] && responses[1].length > 0) {
                const productsByProcessingClass = this.getProductsOfChosenProcessingClass(responses[1]);
                this.products = [...productsByProcessingClass];
              }
              else {
                this.products = [];
              }
              observer.next(true);
            },
            (errorResponse) => {
              observer.next(false);
              const error = errorResponse.error;
              return error.businessMessage;
            });
      }
      else {
        return this.insuredPersonService.getPolicyLinkedPersons(this.selectedPolicy.policyId, this.selectedProduct ? this.selectedProduct.productId : null, this.getProcessingClass(this.type))
          .pipe(
            take(1),
          )
          .subscribe((response: InsuredPersonDTO[]) => {
              if (response && response.length > 0) {
                response.map((person: InsuredPersonDTO) => {
                  person.displayValue = this.getInsuredPersonDisplayValue(person);
                  return person;
                });
                this.filteredInsuredPersons = response;
              }
              observer.next(true);
            },
            (errorResponse) => {
              observer.next(false);
              const error = errorResponse.error;
              return error.businessMessage;
            });
      }
    });
  }

  private loadMembershipsAndProducts$() {
    return new Observable((observer: Observer<any>) => {
      if (!this.selectedInsuredPerson) {
        observer.next(false);
        return;
      }

      return forkJoin(
        this.policyService.getInsuredPersonLinkedPolicies(this.selectedInsuredPerson.insuredPersonId, this.selectedProduct ? this.selectedProduct.productId : null, this.getProcessingClass(this.type)),
        this.productService.getInsuredPersonLinkedProducts(this.selectedInsuredPerson.insuredPersonId, this.selectedPolicy ? this.selectedPolicy.policyId : null, this.getProcessingClass(this.type))
      )
        .pipe(
          take(1),
        )
        .subscribe((responses: [PolicyDTO[], ProductDTO[]]) => {
            if (responses[0] && responses[0].length > 0) {
              this.filteredMemberships = [...responses[0]];
            }
            else {
              this.filteredMemberships = [];
            }

            if (responses[1] && responses[1].length > 0) {
              const productsByProcessingClass = this.getProductsOfChosenProcessingClass(responses[1]);
              this.products = [...productsByProcessingClass];
            }
            else {
              this.products = [];
            }
            observer.next(true);
          },
          (errorResponse) => {
            observer.next(false);
            const error = errorResponse.error;
            return error.businessMessage;
          });
    });
  }

  private showNotifications() {
    const memberOrMembershipValue = this.memberOrMembershipFormControl.value;
    if (memberOrMembershipValue === this.searchType.Membership && this.filteredInsuredPersons.length === 0) {
      this.sharedService.openSnackBar(`No members with a ${this.type} product are assigned to the selected membership`, 'Dismiss');
    }
    if (memberOrMembershipValue === this.searchType.Member && this.filteredMemberships.length === 0) {
      this.sharedService.openSnackBar(`No memberships with a ${this.type} product are assigned to the selected member`, 'Dismiss');
    }
  }

  private getProductsOfChosenProcessingClass(products: ProductDTO[]) {
    if (products?.length) {
      const caseType: string = this.type;
      return products.filter((product: ProductDTO) => product.processingClass.name === caseType);
    }
    return products;
  }

}
